export const DEFAULT_CONFIG_RETRY_DELAYS_MS = [0, 750, 2_000, 5_000, 10_000];
export const DEFAULT_CONFIG_REQUEST_TIMEOUT_MS = 6_000;
const DEFAULT_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1_000;

export async function loadStorefrontConfig({
  url,
  cacheKey,
  fetchImpl = globalThis.fetch,
  storage = globalThis.localStorage,
  retryDelaysMs = DEFAULT_CONFIG_RETRY_DELAYS_MS,
  requestTimeoutMs = DEFAULT_CONFIG_REQUEST_TIMEOUT_MS,
  cacheMaxAgeMs = DEFAULT_CACHE_MAX_AGE_MS,
  now = () => Date.now(),
  sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)),
}) {
  let lastError = new Error("Kunne ikke laste konfigurasjonen.");
  let attempts = 0;

  for (const delayMs of retryDelaysMs) {
    if (delayMs) await sleep(delayMs);
    attempts += 1;
    try {
      const response = await fetchWithTimeout(fetchImpl, url, requestTimeoutMs);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const error = new Error(payload.error || `Konfigurasjonen svarte HTTP ${response.status}.`);
        error.statusCode = response.status;
        error.retriable = response.status === 408 || response.status === 425 || response.status === 429 || response.status >= 500;
        throw error;
      }
      const payload = await response.json();
      if (!payload?.ok) {
        const error = new Error(payload?.error || "Konfigurasjonen er ugyldig.");
        error.retriable = false;
        throw error;
      }
      writeCache(storage, cacheKey, { savedAt: now(), payload });
      return {
        payload,
        source: attempts > 1 ? "network_retry" : "network",
        attempts,
      };
    } catch (error) {
      lastError = normalizeConfigError(error);
      if (lastError.retriable === false) break;
      const cached = readFreshCache(storage, cacheKey, now(), cacheMaxAgeMs);
      if (cached) {
        return {
          payload: cached.payload,
          source: "cache_fallback",
          attempts,
          recoveredFrom: lastError,
        };
      }
    }
  }

  const cached = readFreshCache(storage, cacheKey, now(), cacheMaxAgeMs);
  if (cached) {
    return {
      payload: cached.payload,
      source: "cache_fallback",
      attempts,
      recoveredFrom: lastError,
    };
  }

  lastError.attempts = attempts;
  throw lastError;
}

async function fetchWithTimeout(fetchImpl, url, timeoutMs) {
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(new Error("Konfigurasjonskallet tok for lang tid.")), timeoutMs)
    : null;
  try {
    return await fetchImpl(url, {
      cache: "no-store",
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch (error) {
    const normalized = normalizeConfigError(error);
    normalized.retriable = true;
    throw normalized;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

function normalizeConfigError(error) {
  if (error instanceof Error) return error;
  return new Error(String(error || "Kunne ikke laste konfigurasjonen."));
}

function writeCache(storage, key, value) {
  if (!storage || !key) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in privacy modes; network loading must still work.
  }
}

function readCache(storage, key) {
  if (!storage || !key) return null;
  try {
    return JSON.parse(storage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function readFreshCache(storage, key, currentTime, maximumAge) {
  const cached = readCache(storage, key);
  if (!cached?.payload?.ok) return null;
  return currentTime - Number(cached.savedAt || 0) <= maximumAge ? cached : null;
}
